// Shift detail — the canonical deep-link target from notification emails.
// Shows the full shift (including the note: this IS the approved surface for
// note text), acknowledge, and the request actions (drop / swap / claim).
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../auth/AuthProvider'
import {
  fetchPositions,
  fetchShift,
  fetchTeams,
  fetchTeamWeekShifts,
} from '../../api/reads'
import { api } from '../../api/mutations'
import { CallableError } from '../../api/callable'
import { useToast } from '../../components/Toast'
import { EmptyState, ErrorBanner, Modal, Spinner, StatusChip } from '../../components/ui'
import { addDays, todayLocal, weekKeyFor } from '../../lib/time'
import { formatDateLong, formatDateShort, formatHours, formatTimeRange } from '../../lib/format'
import type { Employee, Shift, Team } from '../../lib/types'

export default function ShiftDetailPage() {
  const { employee, email } = useAuth()
  const { shiftId } = useParams<{ shiftId: string }>()
  if (!employee || !email || !shiftId) return null
  return <ShiftDetail employee={employee} email={email} shiftId={shiftId} />
}

/** Normalized claim-error copy branched on CallableError.serverCode. */
function claimErrorMessage(e: unknown): string {
  if (e instanceof CallableError) {
    if (e.serverCode === 'DOUBLE_BOOKED') return 'That overlaps a shift you already work.'
    if (e.serverCode === 'CREDENTIAL_GAP') {
      const raw = e.details.missing
      const missing = Array.isArray(raw) ? raw.filter((m): m is string => typeof m === 'string') : []
      return missing.length > 0
        ? `You're missing a required credential: ${missing.join(', ')}.`
        : "You're missing a required credential for this position."
    }
    if (e.serverCode === 'PENDING_REQUEST') return 'A request is already pending on this shift.'
    return e.message
  }
  return e instanceof Error ? e.message : 'Something went wrong.'
}

function ShiftDetail({ employee, email, shiftId }: { employee: Employee; email: string; shiftId: string }) {
  const navigate = useNavigate()
  const toast = useToast()
  const queryClient = useQueryClient()
  const departmentId = employee.departmentId

  const shiftQuery = useQuery({
    queryKey: ['shift', shiftId],
    // Permission-denied reads render exactly like bad ids — no scope leakage.
    queryFn: () => fetchShift(shiftId).catch(() => null),
  })

  const referenceQuery = useQuery({
    queryKey: ['reference', departmentId],
    queryFn: async () => {
      const [teams, positions] = await Promise.all([fetchTeams(departmentId!), fetchPositions(departmentId!)])
      return { teams, positions }
    },
    enabled: !!departmentId,
  })

  const [dropOpen, setDropOpen] = useState(false)
  const [dropReason, setDropReason] = useState('')
  const [swapOpen, setSwapOpen] = useState(false)
  const [swapChoice, setSwapChoice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Swap candidates: my home team's published weeks — this week + next two.
  const swapWeekStarts = useMemo(() => {
    const wk = weekKeyFor(todayLocal())
    return [wk, addDays(wk, 7), addDays(wk, 14)]
  }, [])
  const teamId = employee.homeTeamId
  const swapWeekQueries = useQueries({
    queries: swapWeekStarts.map((ws) => ({
      queryKey: ['teamWeek', teamId, ws],
      queryFn: () => fetchTeamWeekShifts(teamId!, ws),
      enabled: swapOpen && !!teamId,
    })),
  })

  if (shiftQuery.isPending) return <Spinner label="Loading shift…" />
  if (shiftQuery.isError) return <ErrorBanner message="Couldn't load this shift. Try again." />

  const shift = shiftQuery.data
  const isMine = !!shift && shift.assigneeEmail === email
  const isHomeTeam = !!shift && shift.teamId === employee.homeTeamId
  const isOpen = !!shift && shift.status === 'open'
  if (!shift || (!isMine && !isHomeTeam && !isOpen)) {
    return <EmptyState title="Shift not found" hint="Check the link, or head back to My Shifts." />
  }

  const teams: Team[] = referenceQuery.data?.teams ?? []
  const teamName = teams.find((t) => t.id === shift.teamId)?.name ?? null
  const now = Date.now()
  const isFuture = Date.parse(shift.startAt) > now
  const canAct =
    isMine && shift.published && shift.status === 'assigned' && isFuture && shift.pendingRequestId === null

  const invalidateAfterRequest = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['shift', shiftId] }),
      queryClient.invalidateQueries({ queryKey: ['myShifts', email] }),
      queryClient.invalidateQueries({ queryKey: ['requests', 'mine', email] }),
    ])
  }

  const acknowledge = async () => {
    setBusy(true)
    try {
      await api.acknowledgeShift({ shiftId: shift.id })
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['shift', shiftId] }),
        queryClient.invalidateQueries({ queryKey: ['myShifts', email] }),
      ])
      toast('Shift confirmed. Thanks!', 'success')
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not confirm the shift.', 'error')
    } finally {
      setBusy(false)
    }
  }

  const submitDrop = async () => {
    const reason = dropReason.trim()
    if (!reason) {
      toast('A reason is required.', 'error')
      return
    }
    setBusy(true)
    try {
      await api.requestDrop({ shiftId: shift.id, reason })
      await invalidateAfterRequest()
      setDropOpen(false)
      setDropReason('')
      toast('Drop request sent to your supervisor.', 'success')
    } catch (e) {
      toast(claimErrorMessage(e), 'error')
    } finally {
      setBusy(false)
    }
  }

  // Candidate counterpart shifts: future, assigned, other people, positions I hold.
  const swapCandidates = swapWeekQueries
    .flatMap((q) => q.data ?? [])
    .filter(
      (s) =>
        s.id !== shift.id &&
        s.status === 'assigned' &&
        s.published &&
        s.assigneeEmail !== null &&
        s.assigneeEmail !== email &&
        s.pendingRequestId === null &&
        employee.positionIds.includes(s.positionId) &&
        Date.parse(s.startAt) > now,
    )
    .sort((a, b) => a.startAt.localeCompare(b.startAt))
  const swapLoading = swapWeekQueries.some((q) => q.isLoading)
  const swapErrored = swapWeekQueries.some((q) => q.isError)

  const submitSwap = async () => {
    const other = swapCandidates.find((s) => s.id === swapChoice)
    if (!other) return
    setBusy(true)
    try {
      await api.requestSwap({ myShiftId: shift.id, otherShiftId: other.id })
      await invalidateAfterRequest()
      setSwapOpen(false)
      setSwapChoice(null)
      toast(`Swap request sent to ${other.assigneeName ?? 'your teammate'}`, 'success')
    } catch (e) {
      toast(claimErrorMessage(e), 'error')
    } finally {
      setBusy(false)
    }
  }

  const submitClaim = async () => {
    setBusy(true)
    try {
      await api.requestClaim({ shiftId: shift.id })
      await Promise.all([
        invalidateAfterRequest(),
        queryClient.invalidateQueries({ queryKey: ['openShifts', departmentId] }),
      ])
      toast('Request sent — your supervisor will review it.', 'success')
    } catch (e) {
      toast(claimErrorMessage(e), 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <button className="btn-ghost !min-h-[40px] px-2" onClick={() => void navigate(-1)}>
        ‹ Back
      </button>

      <section className="card space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <StatusChip status={shift.status} />
          {isMine && <span className="chip bg-lake/20 text-interlochen">Yours</span>}
          {shift.pendingRequestId !== null && <span className="chip bg-sunset/20 text-clay">Request pending</span>}
        </div>

        <div>
          <h1 className="text-xl font-bold text-interlochen">{formatDateLong(shift.date)}</h1>
          <p className="tnum text-lg font-semibold text-ink">
            {formatTimeRange(shift.startTime, shift.endTime, shift.endsNextDay)}
            <span className="tnum ml-2 text-sm font-normal text-stone">{formatHours(shift.durationHours)}</span>
          </p>
          <p className="text-ink">
            {shift.positionName}
            {teamName && <span className="text-stone"> · {teamName}</span>}
          </p>
        </div>

        {shift.notes && (
          <div className="rounded-md bg-sand/40 p-3">
            <p className="section-label mb-1">Note</p>
            <p className="whitespace-pre-wrap text-sm text-ink">{shift.notes}</p>
          </div>
        )}

        {isMine && shift.published && shift.status === 'assigned' && (
          shift.ackedAt !== null ? (
            <p className="text-sm text-forest">✓ Acknowledged {formatDateShort(shift.ackedAt)}</p>
          ) : (
            <div className="flex flex-col gap-2 rounded-md border border-sunset/50 bg-sunset/10 p-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm font-semibold text-clay">New/changed — please confirm</p>
              <button className="btn-primary !min-h-[40px]" onClick={() => void acknowledge()} disabled={busy}>
                Acknowledge
              </button>
            </div>
          )
        )}

        <p className="text-xs text-stone">This shift was emailed to you with a calendar invite.</p>
      </section>

      {shift.pendingRequestId !== null && (
        <p className="rounded-md bg-sunset/10 px-4 py-3 text-sm text-clay">
          A request is already pending on this shift.
        </p>
      )}

      {canAct && (
        <section className="card space-y-2" aria-label="Shift actions">
          <p className="section-label">Need a change?</p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <button className="btn-secondary flex-1" onClick={() => setSwapOpen(true)}>
              Request swap
            </button>
            <button className="btn-danger flex-1" onClick={() => setDropOpen(true)}>
              Can't make it
            </button>
          </div>
        </section>
      )}

      {isOpen && !isMine && shift.published && isFuture && (
        <section className="card space-y-2">
          <p className="text-sm text-stone">
            This shift is open{shift.claimCount > 0 && ` — ${shift.claimCount} other${shift.claimCount > 1 ? 's have' : ' has'} requested it`}.
          </p>
          <button className="btn-primary w-full sm:w-auto" onClick={() => void submitClaim()} disabled={busy}>
            {busy ? 'Sending…' : 'Request this shift'}
          </button>
        </section>
      )}

      <Modal
        open={dropOpen}
        onClose={() => setDropOpen(false)}
        title="Can't make it"
        footer={
          <div className="flex justify-end gap-2">
            <button className="btn-ghost" onClick={() => setDropOpen(false)}>
              Never mind
            </button>
            <button className="btn-danger" onClick={() => void submitDrop()} disabled={busy || !dropReason.trim()}>
              {busy ? 'Sending…' : 'Send drop request'}
            </button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-stone">
            Your supervisor will review this. The shift stays yours until they approve.
          </p>
          <div>
            <label htmlFor="drop-reason" className="section-label mb-1 block">
              Reason (required)
            </label>
            <textarea
              id="drop-reason"
              className="input min-h-[96px]"
              value={dropReason}
              onChange={(e) => setDropReason(e.target.value)}
              placeholder="Why can't you make this shift?"
              required
            />
          </div>
        </div>
      </Modal>

      <Modal
        open={swapOpen}
        onClose={() => setSwapOpen(false)}
        title="Request swap"
        footer={
          <div className="flex justify-end gap-2">
            <button className="btn-ghost" onClick={() => setSwapOpen(false)}>
              Never mind
            </button>
            <button className="btn-primary" onClick={() => void submitSwap()} disabled={busy || !swapChoice}>
              {busy ? 'Sending…' : 'Send swap request'}
            </button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-stone">
            Pick a teammate's shift to trade for yours. They'll get to accept or decline, then a supervisor
            confirms.
          </p>
          {swapLoading && <Spinner label="Finding shifts you could take…" />}
          {swapErrored && <ErrorBanner message="Couldn't load your team's shifts. Try again." />}
          {!swapLoading && !swapErrored && swapCandidates.length === 0 && (
            <EmptyState
              title="No swappable shifts right now"
              hint="Swaps need an upcoming teammate shift in a position you hold."
            />
          )}
          {swapCandidates.length > 0 && (
            <fieldset>
              <legend className="sr-only">Choose a shift to trade for</legend>
              <ul className="space-y-1.5">
                {swapCandidates.map((s: Shift) => (
                  <li key={s.id}>
                    <label
                      className={`flex min-h-[44px] cursor-pointer items-center gap-3 rounded-md border p-3 ${
                        swapChoice === s.id ? 'border-interlochen bg-sky/20' : 'border-hairline bg-white'
                      }`}
                    >
                      <input
                        type="radio"
                        name="swap-candidate"
                        value={s.id}
                        checked={swapChoice === s.id}
                        onChange={() => setSwapChoice(s.id)}
                        className="h-4 w-4 accent-[#004A7B]"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="tnum block text-sm font-semibold text-ink">
                          {formatDateShort(s.date)} · {formatTimeRange(s.startTime, s.endTime, s.endsNextDay)}
                        </span>
                        <span className="block truncate text-sm text-stone">
                          {s.positionName} · {s.assigneeName}
                        </span>
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            </fieldset>
          )}
        </div>
      </Modal>
    </div>
  )
}
